(function () {
  /* Conversion capture — stamps the Cookiebot marketing-consent snapshot plus
     TikTok, Meta and OpenAI click/browser identifiers onto the cart, so they land on
     order.note_attributes for the server-side Conversions Hub (CAPI Worker).
     The consent read is identical to snippets/cookie-consent.liquid: Cookiebot
     is the single authoritative ad-consent source.

     Every resolved consent state is stamped — 'granted' or 'denied' — so a
     missing _ad_consent on an order means the capture never ran (blocked JS,
     buy-it-now bypass, non-web order), never "the shopper declined". The hub's
     coverage metric depends on that distinction.

     THE STAMP IS DEFERRED UNTIL A CART EXISTS. We write only when the `cart`
     cookie already names a cart, and never create one: POST /cart/update.js
     with no usable cart cookie makes Shopify mint a cart and Set-Cookie it,
     which put this script in a cart-creation race with every other app doing
     the same on the landing pageview — the cookie that survived decided which
     cart carried the attribution, and ours could lose. Waiting costs
     no coverage: a cart we created was an empty cart, and an empty cart never
     becomes an order. Dynamic-checkout ("buy now") skips the cart entirely, so
     those orders were never stampable either, pre-created cart or not. The
     click ids are persisted in localStorage with their own TTLs, so they
     survive to whenever a cart appears.

     Consent correctness is unchanged where it can matter: granted and denied
     are both stamped explicitly, and a withdrawal strips the identifiers, as
     soon as a cart exists. With no cart there is no order to carry the wrong
     state, so the question is moot — but the persisted click ids are dropped
     on withdrawal regardless, so a later add-to-cart cannot resurrect them.

     Because the stamp only ever fires on a cart that already exists, and fires
     synchronously on its trigger, there is no pending correction to flush and
     no ownership to verify: no retry budgets, no unload handler, no ownership
     bookkeeping. The stamp itself can still be in flight when the shopper
     leaves, though — a drawer add-to-cart followed straight away by the
     checkout button is exactly that — so the POST goes out keepalive and the
     browser finishes it across the navigation. One request, one attempt. */
  var CONSENT_ATTR = '_ad_consent';
  var TTCLID_ATTR = 'ttclid';
  var TTP_ATTR = '_ttp';
  var TTCLID_STORAGE = 'cc_ttclid';
  var TTCLID_SET_AT = 'cc_ttclid_set_at';
  var FBP_ATTR = '_fbp';
  var FBC_ATTR = '_fbc';
  var FBC_STORAGE = 'cc_fbc';
  var FBC_SET_AT = 'cc_fbc_set_at';
  var OPPREF_ATTR = '_oppref';
  var OPPREF_STORAGE = 'cc_oppref';
  var OPPREF_SET_AT = 'cc_oppref_set_at';
  var LAST_SIG = 'cc_last_attrs';
  /* TikTok click-through attribution window. */
  var TTL_MS = 30 * 24 * 60 * 60 * 1000;
  /* Lifetime of Meta's own _fbc cookie, and the age past which Meta reports the
     fbclid inside one as expired. The synthesised value must not expire sooner,
     and no value older than this is stamped, whichever store it came from. */
  var FBC_TTL_MS = 90 * 24 * 60 * 60 * 1000;
  /* Lifetime of OpenAI's own __oppref cookie ("30 days after the Pixel writes
     the cookie" — developers.openai.com/ads/measurement-pixel). */
  var OPPREF_TTL_MS = 30 * 24 * 60 * 60 * 1000;

  function safeGet(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
  function safeSet(key, value) { try { localStorage.setItem(key, value); } catch (e) {} }
  function safeRemove(key) { try { localStorage.removeItem(key); } catch (e) {} }

  /* Order-status / checkout page — cart attributes are already locked onto the
     order there, so there is nothing to stamp. */
  if (window.Shopify && window.Shopify.Checkout && window.Shopify.Checkout.OrderStatus) {
    return;
  }

  /* Capture ttclid from the landing URL immediately (independent of consent)
     and persist it so it survives to the cart page; sweep once the TTL lapses.
     A swept id is also blanked on the cart by the next stamp — the attrs
     always carry the identifier keys, and an empty value deletes the cart
     attribute (merge semantics would otherwise keep the stale one forever). */
  var urlTtclid = new URLSearchParams(window.location.search).get('ttclid');
  if (urlTtclid) {
    safeSet(TTCLID_STORAGE, urlTtclid);
    safeSet(TTCLID_SET_AT, Date.now().toString());
  } else {
    var setAt = parseInt(safeGet(TTCLID_SET_AT), 10);
    if (setAt && (Date.now() - setAt) > TTL_MS) {
      safeRemove(TTCLID_STORAGE);
      safeRemove(TTCLID_SET_AT);
    }
  }

  /* Meta's click id. fbq writes the _fbc cookie itself when it loads on a
     landing page carrying fbclid, but it only loads after marketing consent —
     a shopper who consents later, or whose fbq load is blocked, lands with
     fbclid and no cookie. Synthesise Meta's documented cookie format
     (fb.<subdomainIndex>.<creationTimeMs>.<fbclid>) so the server leg still
     carries the click. On the pageview that carries fbclid the synthesised
     value is authoritative: fbq has not yet rewritten the cookie when this
     script stamps, so the cookie still holds the PREVIOUS click. On later
     pageviews the real cookie wins. */
  var urlFbclid = new URLSearchParams(window.location.search).get('fbclid');
  var landingFbc = urlFbclid ? 'fb.1.' + Date.now() + '.' + urlFbclid : null;
  if (landingFbc) {
    safeSet(FBC_STORAGE, landingFbc);
    safeSet(FBC_SET_AT, Date.now().toString());
  } else {
    var fbcSetAt = parseInt(safeGet(FBC_SET_AT), 10);
    if (fbcSetAt && (Date.now() - fbcSetAt) > FBC_TTL_MS) {
      safeRemove(FBC_STORAGE);
      safeRemove(FBC_SET_AT);
    }
  }

  /* OpenAI's click id. Same mechanics as ttclid. The oaiq SDK writes __oppref
     itself, but only after it loads under marketing consent, so on the
     pageview carrying the param the URL value is authoritative; on later
     pageviews the SDK's cookie wins, with the stored value covering an SDK
     that never loaded — the same reasoning as _fbc. */
  var urlOppref = new URLSearchParams(window.location.search).get('oppref');
  if (urlOppref) {
    safeSet(OPPREF_STORAGE, urlOppref);
    safeSet(OPPREF_SET_AT, Date.now().toString());
  } else {
    var opprefSetAt = parseInt(safeGet(OPPREF_SET_AT), 10);
    if (opprefSetAt && (Date.now() - opprefSetAt) > OPPREF_TTL_MS) {
      safeRemove(OPPREF_STORAGE);
      safeRemove(OPPREF_SET_AT);
    }
  }

  function readCookie(name) {
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      var idx = parts[i].indexOf('=');
      if (idx > -1 && parts[i].slice(0, idx) === name) {
        return decodeURIComponent(parts[i].slice(idx + 1));
      }
    }
    return null;
  }

  /* The _fbc cookie can outlive the click it encodes (the hub's ledger held
     one 190 days old), so a value is judged on its own creation time at stamp
     time rather than on when this script first saw it. A value that does not
     parse as fb.<idx>.<ms>.<id> is passed through unjudged — the diagnostic
     this guards against is about age, not shape. */
  function freshFbc(value) {
    if (!value) return null;
    var match = /^fb\.\d+\.(\d{13})\./.exec(value);
    if (match && (Date.now() - parseInt(match[1], 10)) > FBC_TTL_MS) return null;
    return value;
  }

  /* The cart this session holds, or null when there is nothing to stamp.
     Shopify's cart cookie is `<token>?key=<key>`; absent, empty, or key-only
     all mean no cart — an empty string is a real case (a cleared cookie), and
     reading it as a token would make every stamp target a cart that cannot
     exist. */
  function cartToken() {
    var raw = readCookie('cart');
    if (!raw) return null;
    return raw.split('?')[0] || null;
  }

  /* Authoritative marketing-consent read — mirrors snippets/cookie-consent.liquid. */
  function hasMarketingConsent() {
    return !!(window.Cookiebot && Cookiebot.consent && Cookiebot.consent.marketing);
  }

  /* Has the shopper answered the banner at all? hasMarketingConsent() reads false for an
     unanswered banner and for a decline alike, so anything that stamps without its own
     resolution has to ask this first: stamping an unresolved state writes 'denied' for a
     shopper who never declined, and the denied branch's sweep then destroys the persisted
     click ids, so the eventual accept stamps an empty ttclid. */
  function consentResolved() {
    return !!(window.Cookiebot && Cookiebot.hasResponse);
  }

  /* The dedup signature includes the cart token: Shopify replaces the cart
     after every completed checkout, and the fresh cart starts attribute-less —
     a device-scoped signature would skip stamping every repeat purchase. */
  function signature(attrs, token) {
    return JSON.stringify(attrs) + '|' + token;
  }

  var inflight = false;
  var queued = false;
  var lateCookieRetry = false;
  /* fbq/ttq write _fbp/_ttp only after consent and a vendor script load, which
     is later than the consent event this script stamps on. One delayed re-stamp
     catches the single-pageview session; the signature check makes it a no-op
     when nothing changed. */
  var LATE_COOKIE_RETRY_MS = 4000;

  function updateCart(attrs) {
    var token = cartToken();
    /* No cart: do nothing at all. Not an error and not a retry case — the
       consent and cart-mutation triggers below re-run this the moment one
       exists, and until then there is no order for the stamp to reach. */
    if (!token) return;
    var sig = signature(attrs, token);
    var last = safeGet(LAST_SIG);
    /* Signatures stored before this file switched to the bare token carried the whole
       `<token>?key=…` cookie. Accept that shape too, or the deploy makes every returning
       visitor with an unchanged cart write once more for nothing. Same cookie means same
       token, so this can never dedup something the current format would have written. The
       next real change replaces it with the current shape. */
    if (last === sig || last === signature(attrs, readCookie('cart'))) return;
    if (inflight) { queued = true; return; }
    inflight = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
      /* The add-to-cart trigger fires in a drawer, so the shopper can hit checkout while this
         request is still open and the navigation would abort it. The body is a handful of ids,
         far inside the 64KB keepalive limit. Nothing reads the response after a navigation —
         the signature write simply does not happen, and the next trigger stamps again. */
      keepalive: true,
    })
      .then(function (res) {
        /* Only a 2xx counts as stamped; anything else leaves the signature
           unset so the next trigger retries. The signature binds to the token
           read before the request went out — the cart Shopify actually routed
           it to. If something replaced the cookie meanwhile, the attributes
           landed on the old cart and the mismatch makes the next trigger stamp
           the survivor, with no ownership bookkeeping to get it wrong. */
        if (res.ok) safeSet(LAST_SIG, sig);
      })
      .catch(function () {}) /* fail silent to the shopper; retry via unset signature */
      .then(function () {
        inflight = false;
        if (queued) { queued = false; stamp(); }
      });
  }

  function stamp() {
    var granted = hasMarketingConsent();
    var attrs = {};
    attrs[CONSENT_ATTR] = granted ? 'granted' : 'denied';

    if (granted) {
      attrs[TTCLID_ATTR] = safeGet(TTCLID_STORAGE) || '';
      attrs[TTP_ATTR] = readCookie('_ttp') || '';
      attrs[FBP_ATTR] = readCookie('_fbp') || '';
      attrs[FBC_ATTR] = landingFbc || freshFbc(readCookie('_fbc')) || freshFbc(safeGet(FBC_STORAGE)) || '';
      attrs[OPPREF_ATTR] = urlOppref || readCookie('__oppref') || safeGet(OPPREF_STORAGE) || '';
      /* Only worth re-reading the vendor cookies if there is a cart to stamp
         them onto; otherwise the one retry would be spent on a pageview that
         could not have written anything. */
      if (!lateCookieRetry && cartToken() && (!attrs[FBP_ATTR] || !attrs[TTP_ATTR])) {
        lateCookieRetry = true;
        setTimeout(stamp, LATE_COOKIE_RETRY_MS);
      }
    } else {
      /* Denied (fresh or withdrawn) — strip identifiers everywhere so the
         order carries no ad ids without consent. The localStorage sweep runs
         with or without a cart: it is what stops a later add-to-cart stamping
         ids that were captured before the withdrawal. */
      safeRemove(TTCLID_STORAGE);
      safeRemove(TTCLID_SET_AT);
      safeRemove(FBC_STORAGE);
      safeRemove(FBC_SET_AT);
      safeRemove(OPPREF_STORAGE);
      safeRemove(OPPREF_SET_AT);
      attrs[TTCLID_ATTR] = '';
      attrs[TTP_ATTR] = '';
      attrs[FBP_ATTR] = '';
      attrs[FBC_ATTR] = '';
      attrs[OPPREF_ATTR] = '';
    }

    updateCart(attrs);
  }

  /* Fires once consent is resolved — on every load and on fresh accept/decline —
     so the cart is re-stamped whenever marketing consent changes. This script
     is deferred, so for returning visitors Cookiebot may have resolved stored
     consent before the listener exists; hasResponse catches that case without
     ever stamping an unresolved state. */
  window.addEventListener('CookiebotOnConsentReady', stamp);
  if (consentResolved()) stamp();

  /* Every trigger that is not a consent event goes through here. A cart mutation says nothing
     about consent, so it may only stamp what Cookiebot has already resolved; before that the
     consent listener above is the one that will fire, and it will find the cart waiting. */
  function stampIfConsentResolved() {
    if (consentResolved()) stamp();
  }

  /* The other half of the deferred stamp: the shopper's own add-to-cart is what
     creates the cart, and with a drawer cart it happens without a pageview, so
     consent events alone would miss the whole session.

     This theme forked Dawn before pubsub, and has no general cart event —
     /cart/add and /cart/change are called from seven unrelated places
     (product-form, cart, quick-add, ...). cart:item-added covers only adds
     made through product-form and threed.js, never changes or clears. The
     one signal every path shares is the fetch itself, so observe it, exactly as
     assets/cart-shipping-tier.js already does for cart weight. The wrapper is a
     pure pass-through: it returns the original promise untouched and never
     reads the body.

     /cart/update.js is excluded because it is the endpoint our own stamp uses:
     observing it would make every write trigger the next one. That exclusion
     is a real cost, not a free one — /cart/update.js does change contents when
     it is handed an `updates` quantity map — but no caller in this theme sends
     one. Every caller today either writes attributes only (this file,
     cart-shipping-tier.js, referral-tracking.js), writes `note` only
     (cart.js's CartNote), or sends `sections`/`sections_url` as a pure read
     (standard-actions-override.js). */
  var CART_MUTATION = /\/cart\/(add|change|clear)(\.js)?($|\?)/;
  if (window.fetch && !window.__conversionCaptureFetchWrapped) {
    window.__conversionCaptureFetchWrapped = true;
    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var promise = nativeFetch.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (CART_MUTATION.test(url)) {
          promise
            .then(function (res) {
              if (res && res.ok) stampIfConsentResolved();
            })
            .catch(function () {});
        }
      } catch (e) {
        /* Never let observation interfere with the caller's request. */
      }
      return promise;
    };
  }

  /* Two cart mutations in this theme do not go through fetch, so the wrapper cannot see them.
     Rather than teach this file about each one, they announce themselves on a single event any
     future non-fetch caller can reuse.

     assets/threed.js POSTs /cart/add.js over XMLHttpRequest (the 3D configurator, live on
     templates/product.threed.json) and dispatches this, then cart:item-added, from its
     success handler. Its handler
     goes on to submit the cart-drawer form, which navigates to /cart and would stamp there
     anyway — but that navigation is incidental, not a contract, and the click()/submit() calls
     that follow the dispatch can still throw on a null ref. The dispatch survives that either
     way: dispatchEvent reports a listener's exception to window.onerror rather than rethrowing
     it at the call site, so nothing this listener does can unwind back into that handler.

     assets/referral-tracking.js mints a cart on a ?ref= landing by POSTing /cart/update.js
     with the referral attributes, and deliberately does NOT dispatch. That cart is empty, and
     an empty cart never becomes an order — the same reasoning this file's own deferral rests
     on. Every path that puts the first item in it is a trigger we already have: a fetch add
     (the wrapper), the 3D configurator (this event), or a non-JS form post (a navigation, so
     the next pageview's consent trigger). Stamping it at mint time would write to a cart that
     cannot carry an order, which is the write this PR exists to stop making. */
  document.addEventListener('cart:mutated', stampIfConsentResolved);
})();
